import { TimeRange } from '@/infra/time-range'

export interface SchedulableDay {
  /**
   * The date of the day.
   */
  date: Date

  /**
   * The time slots that are available for the day.
   */
  timeSlots: TimeRange[]
}

/**
 * Options for generating schedulable days.
 */
export interface GenerateSchedulableDaysOptions {
  /**
   * The current date and time.
   */
  now: Date

  /**
   * The number of days to show.
   * This is used in conjunction with the earliest time to determine the last day to show.
   */
  daysToShow: number

  /**
   * The interval in minutes between time slots.
   */
  intervalInMinutes: number

  /**
   * The hour of the day when scheduling starts (0-23).
   * Time slots will start at this hour.
   */
  startingHour: number

  /**
   * The hour of the day when scheduling ends (0-23).
   * Time slots will end at this hour.
   */
  endingHour: number

  /**
   * The earliest point in time from where to start generating schedulable days,
   * including the time slots within the first day.
   *
   * For example, if the earliest time is tomorrow at 10:00 AM, then the first day will have
   * time slots starting from 10:00 AM to 9:00 PM, rather than from today 8:00 AM which is
   * the usual start time of a day and earliest possible time slot.
   *
   * If the time part of this date does not fall exactly on a schedulable time slot,
   * the nearest future available time slot will be used.
   */
  earliestTime?: Date
}

/**
 * Generates schedulable days.
 *
 * @param options The options for generating schedulable days.
 * @returns The schedulable days.
 */
export function generateSchedulableDays(
  options: GenerateSchedulableDaysOptions,
): SchedulableDay[] {
  const {
    now,
    daysToShow,
    intervalInMinutes,
    earliestTime,
    startingHour = 8,
    endingHour = 21,
  } = options
  const schedulableDays: SchedulableDay[] = []

  // Determine starting date - use today if no earliestTime is specified,
  // or use the date component of earliestTime if provided
  let startDate = new Date(now)
  startDate.setHours(0, 0, 0, 0) // Reset to beginning of the day

  // If earliestTime is provided, use that date as the starting point
  if (earliestTime) {
    const earliestDate = new Date(earliestTime)
    earliestDate.setHours(0, 0, 0, 0) // Reset to beginning of the day
    if (earliestDate > startDate) {
      startDate = earliestDate
    }
  }

  // Tracks how much to offset the start date by to get the relevant day
  // while iterating.
  let dayOffset = 0

  // Loop for exactly the requested number of days
  while (schedulableDays.length < daysToShow) {
    const dayDate = new Date(startDate)
    dayDate.setDate(dayDate.getDate() + dayOffset)

    // Check if this day needs to be skipped due to the time being too late
    let shouldSkipDay = false

    if (
      earliestTime &&
      dayDate.toDateString() === earliestTime.toDateString() &&
      earliestTime.getHours() >= endingHour
    ) {
      shouldSkipDay = true
    }

    if (shouldSkipDay) {
      dayOffset++
      continue
    }

    // Generate all possible time slots for this day
    let slotStartHour = startingHour
    let slotStartMinute = 0

    // If this is the same day as earliestTime, adjust the start time
    if (
      earliestTime &&
      dayDate.toDateString() === earliestTime.toDateString()
    ) {
      // Use the earliestTime's hour and minute, rounded up to the next slot boundary
      const earliestHour = earliestTime.getHours()
      const earliestMinute = earliestTime.getMinutes()

      // Calculate the nearest future time slot
      if (earliestMinute % intervalInMinutes === 0) {
        // Exactly on a slot boundary
        slotStartHour = Math.max(earliestHour, startingHour)
        slotStartMinute = earliestMinute
      } else {
        if (earliestHour < startingHour) {
          // If the earliest time is before the starting hour, use the starting hour
          slotStartHour = startingHour
          slotStartMinute = 0
        } else {
          // Round up to the next slot boundary
          slotStartHour = earliestHour
          slotStartMinute =
            Math.ceil(earliestMinute / intervalInMinutes) * intervalInMinutes

          // If we went past 60 minutes, increment the hour
          if (slotStartMinute >= 60) {
            slotStartHour++
            slotStartMinute = 0
          }
        }
      }
    }

    // Generate time slots using the helper function
    const timeSlots = generateDayTimeSlots(
      dayDate,
      slotStartHour,
      slotStartMinute,
      endingHour,
      intervalInMinutes,
    )

    // Only add days that have time slots
    if (timeSlots.length > 0) {
      schedulableDays.push({
        date: dayDate,
        timeSlots,
      })
    }

    dayOffset++
  }

  return schedulableDays
}

/**
 * Generates time slots for a single day.
 *
 * @param date The date to generate time slots for
 * @param startHour The hour to start generating from
 * @param startMinute The minute to start generating from
 * @param endHour The hour to stop generating at
 * @param intervalInMinutes The interval between slots in minutes
 * @returns Array of TimeRange objects representing the time slots
 */
function generateDayTimeSlots(
  date: Date,
  startHour: number,
  startMinute: number,
  endHour: number,
  intervalInMinutes: number,
): TimeRange[] {
  const timeSlots: TimeRange[] = []

  // Generate time slots from the determined start time to the end of day
  let cursor = new Date(date)
  cursor.setHours(startHour, startMinute, 0, 0)

  const endTime = new Date(date)
  endTime.setHours(endHour, 0, 0, 0)

  while (cursor < endTime) {
    const slotEnd = new Date(cursor)
    slotEnd.setMinutes(cursor.getMinutes() + intervalInMinutes)

    if (slotEnd <= endTime) {
      timeSlots.push(TimeRange.of(new Date(cursor), new Date(slotEnd)))
    }

    cursor = slotEnd
  }

  return timeSlots
}
